// Turns a policy page's stored HTML (Privacy Policy, Terms of Service, …) into
// what the document layout needs: the body with anchored sections, the list of
// those sections for the table of contents, and the "last updated" date lifted
// out of the body so it can sit under the title.
//
// The HTML comes from three places — the migration boilerplate, the site
// wizard's generated policies, and hand-edited pages in the admin — so nothing
// here assumes a particular shape. A page with no <h2> simply has no contents
// list.

export interface PolicySection {
  id: string;
  title: string;
}

export interface PolicyDocument {
  html: string;
  sections: PolicySection[];
  /** The date text from a leading "Last updated: …" line, if the page had one. */
  lastUpdated: string | null;
}

/**
 * Slugs that are legal / policy documents and get the document layout. The
 * pattern covers the pages every site ships with (privacy-policy,
 * terms-of-service, returns, shipping) and the per-site-type ones the wizard
 * adds (cancellation-policy, donation-receipt, membership-terms,
 * responsible-play, age-verification, sms-terms).
 */
const POLICY_SLUG = /^(privacy|terms)(-|$)|(-policy|-terms)$|^(returns|shipping|donation-receipt|responsible-play|age-verification)$/;

export function isPolicySlug(slug: string | undefined | null): boolean {
  return !!slug && POLICY_SLUG.test(slug);
}

const LAST_UPDATED = /^\s*(?:last\s+updated|effective(?:\s+date)?|updated)\s*[:\-–—]?\s*(.+?)\s*$/i;

function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export function preparePolicyDocument(rawHtml: string): PolicyDocument {
  if (!rawHtml || typeof DOMParser === 'undefined') {
    return { html: rawHtml || '', sections: [], lastUpdated: null };
  }
  const doc = new DOMParser().parseFromString(`<div>${rawHtml}</div>`, 'text/html');
  const root = doc.body.firstElementChild as HTMLElement | null;
  if (!root) return { html: rawHtml, sections: [], lastUpdated: null };

  // The layout renders the page title itself; a leading <h1> in the body
  // (generated policies sometimes open with one) would repeat it.
  const first = root.firstElementChild;
  if (first?.tagName === 'H1') first.remove();

  // "Last updated: July 25, 2026" as the opening line → shown under the title.
  let lastUpdated: string | null = null;
  const opener = root.firstElementChild;
  if (opener && opener.tagName === 'P' && opener.children.length <= 1) {
    const match = (opener.textContent ?? '').match(LAST_UPDATED);
    if (match && match[1].length <= 40) {
      lastUpdated = match[1].replace(/[.\s]+$/, '');
      opener.remove();
    }
  }

  const sections: PolicySection[] = [];
  const used = new Set<string>();
  root.querySelectorAll('h2').forEach((h2, i) => {
    const title = (h2.textContent ?? '').trim();
    if (!title) return;
    let id = slugify(title) || `section-${i + 1}`;
    // Ids must be unique, and must not start with a digit-only token that
    // collides between "1. Scope" style headings on long pages.
    let n = 2;
    const base = id;
    while (used.has(id)) id = `${base}-${n++}`;
    used.add(id);
    h2.id = id;
    sections.push({ id, title });
  });

  return { html: root.innerHTML, sections, lastUpdated };
}
